import type {
  AccountCategoryListResponse,
  AccountCategoryAttributesResponse,
  AccountCategoryAttribute,
  MarketplaceType,
} from '../types/AccountCategory';
import { HttpClient } from 'src/app/modules/core';

class AccountCategoryService {
  private static instance: AccountCategoryService;
  private http: HttpClient;

  constructor() {
    this.http = HttpClient.getInstance();
  }

  static getInstance(): AccountCategoryService {
    if (!this.instance) {
      this.instance = new AccountCategoryService();
    }
    return this.instance;
  }

  /**
   * Get account categories for a marketplace
   */
  async getAccountCategories(
    marketplace: MarketplaceType,
    options: {
      page?: number;
      limit?: number;
      search?: string;
      useCache?: boolean;
      parentId?: string | null;
    } = {}
  ): Promise<AccountCategoryListResponse> {
    // Validate marketplace
    const validMarketplaces = ['shopee', 'lazada', 'tiktok'];
    if (!validMarketplaces.includes(marketplace)) {
      throw new Error(`Invalid marketplace: ${marketplace}. Must be one of: ${validMarketplaces.join(', ')}`);
    }

    const { page = 1, limit = 50, search, useCache = true, parentId } = options;

    // Validate parameters
    if (page < 1) {
      throw new Error('Page number must be greater than or equal to 1');
    }
    if (limit < 1 || limit > 100) {
      throw new Error('Limit must be between 1 and 100');
    }
    if (search && search.length > 100) {
      throw new Error('Search term cannot exceed 100 characters');
    }

    let endpoint = `/api/v2/marketplace/${marketplace}/categories?page=${page}&limit=${limit}`;

    if (search) {
      endpoint += `&search=${encodeURIComponent(search)}`;
    }

    // Add parentId parameter for hierarchical navigation
    if (parentId !== undefined) {
      endpoint += `&parent_id=${parentId === null ? 'null' : encodeURIComponent(parentId)}`;
    }

    const result = await this.http.get<AccountCategoryListResponse>(
      endpoint,
      undefined,
      { useCache }
    );

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch categories');
    }

    // Transform categories to camelCase
    const transformedData: AccountCategoryListResponse = {
      ...result.data!,
      data: {
        categories: result.data!.data.categories.map(cat =>
          this.transformCategoryData(cat)
        )
      }
    };

    return transformedData;
  }

  /**
   * Get root categories (categories with no parent)
   */
  async getRootCategories(
    marketplace: MarketplaceType,
    options: { limit?: number; useCache?: boolean } = {}
  ): Promise<AccountCategoryListResponse> {
    return this.getAccountCategories(marketplace, {
      ...options,
      parentId: null,
      limit: options.limit || 100,
    });
  }

  /**
   * Get child categories for a specific parent category
   */
  async getChildCategories(
    marketplace: MarketplaceType,
    parentId: string,
    options: { limit?: number; useCache?: boolean } = {}
  ): Promise<AccountCategoryListResponse> {
    return this.getAccountCategories(marketplace, {
      ...options,
      parentId,
      limit: options.limit || 100,
    });
  }

  /**
   * Convert various boolean representations to a proper boolean value
   */
  private normalizeBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    if (typeof value === 'number') return value === 1;
    return false;
  }

  /**
   * Transform attribute data to ensure consistent field naming
   * Handles both snake_case (from API) and camelCase (frontend expected) formats
   */
  private transformAttributeData(attr: any): AccountCategoryAttribute {
    // Get the mandatory field value with proper boolean conversion
    const mandatoryValue = attr.isMandatory ?? attr.is_mandatory ?? false;
    const isMandatory = this.normalizeBoolean(mandatoryValue);

    const transformedAttribute = {
      attributeId: attr.attributeId || attr.attribute_id,
      originalAttributeName: attr.originalAttributeName || attr.original_attribute_name || '',
      displayAttributeName: attr.displayAttributeName || attr.display_attribute_name || attr.originalAttributeName || attr.original_attribute_name || '',
      isMandatory,
      inputValidation: attr.inputValidation || attr.input_validation || 'STRING_TYPE',
      formatType: attr.formatType || attr.format_type || 'NORMAL',
      inputType: attr.inputType || attr.input_type || 'TEXT_FIELD',
      maxInputValueNumber: attr.maxInputValueNumber || attr.max_input_value_number || 1,
      attributeUnit: attr.attributeUnit || attr.attribute_unit || [],
      attributeValues: (attr.attributeValues || attr.attribute_values || []).map((value: any) => ({
        valueId: value.valueId || value.value_id,
        originalValueName: value.originalValueName || value.original_value_name || '',
        displayValueName: value.displayValueName || value.display_value_name || value.originalValueName || value.original_value_name || ''
      }))
    };

    return transformedAttribute;
  }

  /**
   * Transform category data from API snake_case to frontend camelCase
   * Ensures consistent field naming across the application
   * Handles multi-language categoryName object: { en: "...", id: "..." }
   * Stores both language variants for reactive UI language switching
   */
  private transformCategoryData(category: any): any {
    // Extract localized names from multi-language object
    // Server returns: { categoryName: { en: "Mobile Phones", id: "Ponsel" } }
    let nameEn = ''
    let nameId = ''
    let displayName = category.name || ''

    if (category.categoryName) {
      if (typeof category.categoryName === 'object') {
        // Multi-language object - extract both languages
        nameEn = category.categoryName.en || ''
        nameId = category.categoryName.id || ''
        // Default display name: prefer English, fallback to Indonesian
        displayName = nameEn || nameId || category.name || ''
      } else {
        // Simple string format - use for all languages
        displayName = category.categoryName
        nameEn = category.categoryName
        nameId = category.categoryName
      }
    }

    return {
      id: category.id || category.categoryId,
      name: displayName,  // Default display name (backward compatible)
      nameLocalized: {
        en: nameEn,
        id: nameId
      },
      hasChildren: category.has_children ?? category.hasChildren ?? false,
      isActive: category.is_active ?? category.isActive ?? true,
      parentCategoryId: category.parent_category_id ?? category.parentCategoryId,
    };
  }

  /**
   * Get category attributes for a specific category
   */
  async getAccountCategoryAttributes(
    marketplace: string,
    categoryId: string
  ): Promise<AccountCategoryAttributesResponse> {
    // Validate inputs
    if (!marketplace) {
      throw new Error('Marketplace is required');
    }
    if (!categoryId) {
      throw new Error('Category ID is required');
    }
    if (categoryId.length > 50) {
      throw new Error('Category ID cannot exceed 50 characters');
    }

    const endpoint = `/api/v2/marketplace/${marketplace}/categories/${categoryId}/attributes`;

    const result = await this.http.get<any>(endpoint);

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch category attributes');
    }

    const rawData = result.data;
    
    // Transform attributes to ensure consistent field naming
    const transformedData: AccountCategoryAttributesResponse = {
      ...rawData,
      data: {
        ...rawData.data,
        attributes: (rawData.data.attributes || []).map((attr: any) => this.transformAttributeData(attr))
      }
    };

    return transformedData;
  }

  /**
   * Get the full hierarchical path for a specific category
   */
  async getCategoryPath(
    marketplace: string,
    categoryId: string
  ): Promise<any> { // Replace 'any' with a proper PathResponse type
    if (!marketplace || !categoryId) {
      throw new Error('Marketplace and Category ID are required');
    }

    const endpoint = `/api/v2/marketplace/${marketplace}/categories/${categoryId}/path`;
    const result = await this.http.get<any>(endpoint);

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch category path');
    }

    // Transform path categories to camelCase
    if (result.data?.data?.path) {
      result.data.data.path = result.data.data.path.map((cat: any) =>
        this.transformCategoryData(cat)
      );
    }

    return result.data;
  }

  /**
   * Get all categories for a marketplace (handles pagination automatically)
   */
  async getAllAccountCategories(
    marketplace: MarketplaceType,
    search?: string
  ): Promise<AccountCategoryListResponse> {
    try {
      // Fetch first page with maximum page size
      const firstResponse = await this.getAccountCategories(marketplace, {
        page: 1,
        limit: 100,
        search,
        useCache: true
      });

      // If all data fits in one page, return it
      const { totalPages, total } = firstResponse.pagination;
      if (totalPages <= 1) {
        return firstResponse;
      }

      // Fetch remaining pages in parallel
      const allCategories = [...firstResponse.data.categories];
      const pagePromises: Promise<AccountCategoryListResponse>[] = [];

      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(
          this.getAccountCategories(marketplace, {
            page,
            limit: 100,
            search,
            useCache: true
          })
        );
      }

      const remainingResponses = await Promise.all(pagePromises);
      remainingResponses.forEach(response => {
        allCategories.push(...response.data.categories);
      });

      // Return combined response
      return {
        ...firstResponse,
        data: {
          categories: allCategories
        },
        pagination: {
          ...firstResponse.pagination,
          page: 1,
          limit: total,
          totalPages: 1
        }
      };
    } catch (error) {
      console.error(`Failed to get all categories for ${marketplace}:`, error);
      throw error;
    }
  }

  /**
   * Search categories across all marketplaces
   */
  async searchAccountCategories(
    searchTerm: string,
    marketplaces: MarketplaceType[] = ['shopee', 'lazada', 'tiktok']
  ): Promise<Record<string, AccountCategoryListResponse>> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      throw new Error('Search term is required');
    }

    const results: Record<string, AccountCategoryListResponse> = {};
    const promises = marketplaces.map(async (marketplace) => {
      try {
        const response = await this.getAccountCategories(marketplace, {
          search: searchTerm,
          limit: 50
        });
        results[marketplace] = response;
      } catch (error) {
        console.error(`Failed to search categories for ${marketplace}:`, error);
        // Don't throw, just log the error and continue with other marketplaces
      }
    });

    await Promise.all(promises);
    return results;
  }

  /**
   * Clear cache for categories
   */
  clearCache(marketplace?: string): void {
    if (marketplace) {
      // Clear cache for specific marketplace
      this.http.clearCache(`/${marketplace}/categories`);
    } else {
      // Clear all category-related cache
      this.http.clearCache('/categories');
    }
  }
}

export default AccountCategoryService;
